import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { fetchWithAuth } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import usePoll from "../../hooks/usePoll";

/**
 * Sitewide alerts (Settings > Alert Center) — one shared feed for the whole
 * admin shell. The provider polls /admin/alerts/active every minute (plus on
 * tab focus/visibility, mirroring DailyMessage) and exposes:
 *   alerts      — the alerts targeted at the current user, newest first;
 *                 each carries a per-user `read` flag (inbox seen-state)
 *   unreadCount — alerts not yet marked read (feeds the bell badge)
 *   markRead    — mark one alert read (it keeps showing, just no badge/dot)
 *   markAllRead — inbox "Mark all as read"
 *   dismiss     — optimistically hide a dismissable alert (server-persisted)
 *   refresh     — force a refetch (Alert Center calls this after admin changes)
 *
 * Consumed by SiteAlertBanners (top strips), SiteAlertCards (bottom-right
 * stack), and InboxMenu (pinned section in the header inbox dropdown).
 */

const SiteAlertsContext = createContext({
  alerts: [],
  unreadCount: 0,
  markRead: () => {},
  markAllRead: () => {},
  dismiss: () => {},
  refresh: () => {},
});

export const useSiteAlerts = () => useContext(SiteAlertsContext);

const POLL_MS = 60_000;

export function SiteAlertsProvider({ children }) {
  const { can } = useAuth() || {};
  const canView = typeof can === "function" && can("alerts.view");
  const [alerts, setAlerts] = useState([]);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!canView || inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await fetchWithAuth("/admin/alerts/active");
      if (res.ok) {
        const data = await res.json();
        setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
      } else {
        return false; // usePoll backs off while the API struggles
      }
    } catch {
      // transient failure — keep showing the last known alerts
    } finally {
      inFlight.current = false;
    }
  }, [canView]);

  const pollNow = usePoll(refresh, POLL_MS, { enabled: canView, refreshOnVisible: true });
  useEffect(() => {
    if (!canView) {
      setAlerts([]);
      return undefined;
    }
    // Window focus also refetches (a click back into an already-visible window
    // doesn't fire visibilitychange) — through the poll's own trigger, so it
    // can never overlap a tick.
    window.addEventListener("focus", pollNow);
    return () => window.removeEventListener("focus", pollNow);
  }, [canView, pollNow]);

  const dismiss = useCallback(
    async (id) => {
      // Optimistic: hide immediately; resync if the server refuses (e.g. the
      // alert was escalated to non-dismissable since the last poll).
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      try {
        const res = await fetchWithAuth(`/admin/alerts/${id}/dismiss`, {
          method: "POST",
        });
        if (!res.ok) refresh();
      } catch {
        refresh();
      }
    },
    [refresh]
  );

  const markRead = useCallback(async (id) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
    try {
      await fetchWithAuth(`/admin/alerts/${id}/read`, { method: "POST" });
    } catch {
      // best effort — the next poll resyncs
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    try {
      await fetchWithAuth("/admin/alerts/read-all", { method: "POST" });
    } catch {
      // best effort — the next poll resyncs
    }
  }, []);

  const unreadCount = alerts.filter((a) => !a.read).length;

  return (
    <SiteAlertsContext.Provider
      value={{ alerts, unreadCount, markRead, markAllRead, dismiss, refresh }}
    >
      {children}
    </SiteAlertsContext.Provider>
  );
}
